import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import Sidebar from '../../components/layout/Sidebar';
import Header from '../../components/layout/Header';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { 
  Plus, 
  Search, 
  Filter,
  FolderKanban,
  Clock,
  MoreHorizontal,
  Users,
  UserPlus,
  Building2,
  LayoutGrid,
  Table2,
  TrendingUp,
  TrendingDown,
  Timer,
  DollarSign,
  Play,
  Rocket
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '../../components/ui/tooltip';
import api from '../../lib/axios';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';
import { useNavigate } from 'react-router-dom';

const ProjectsPage = () => {
  const { user, isAgency, isClient, isTeamMember } = useAuth();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [activeTimers, setActiveTimers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [viewMode, setViewMode] = useState('table'); // Default to table view (Monday style)
  const [isMobileView, setIsMobileView] = useState(window.innerWidth < 768);
  
  // Detect mobile and force grid view
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobileView(mobile);
      if (mobile && viewMode === 'table') {
        setViewMode('grid');
      }
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [viewMode]);
  const [showNewProject, setShowNewProject] = useState(false);
  const [showNewClientModal, setShowNewClientModal] = useState(false);
  const [clientMode, setClientMode] = useState('existing'); // 'existing' or 'new'
  const [newProject, setNewProject] = useState({
    name: '',
    project_type: 'logo',
    client_id: '',
    description: '',
    estimated_hours: ''
  });
  const [newClient, setNewClient] = useState({
    first_name: '',
    last_name: '',
    email: '',
    company_name: '',
    phone: ''
  });
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    fetchData();
    // Poll for active timers every 30 seconds
    const timerInterval = setInterval(fetchActiveTimers, 30000);
    return () => clearInterval(timerInterval);
  }, []);

  const fetchData = async () => {
    try {
      const [projectsRes] = await Promise.all([
        api.get('/projects/')
      ]);
      setProjects(projectsRes.data);
      
      if (isAgency) {
        const [clientsRes, timersRes] = await Promise.all([
          api.get('/users/', { params: { role: 'client' } }),
          api.get('/time/active-timers').catch(() => ({ data: [] }))
        ]);
        setClients(clientsRes.data);
        setActiveTimers(timersRes.data || []);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchActiveTimers = async () => {
    if (!isAgency) return;
    try {
      const res = await api.get('/time/active-timers');
      setActiveTimers(res.data || []);
    } catch (error) {
      // Silently fail
    }
  };

  const createProject = async (e) => {
    e.preventDefault();
    setCreating(true);
    
    try {
      let clientId = newProject.client_id;
      
      // If creating a new client
      if (clientMode === 'new' && newClient.email) {
        const clientRes = await api.post('/users/client', {
          email: newClient.email,
          first_name: newClient.first_name || 'Client',
          last_name: newClient.last_name || '',
          company_name: newClient.company_name,
          phone: newClient.phone
        });
        clientId = clientRes.data.id;
        toast.success('Nouveau client créé !');
        // Refresh clients list
        const clientsRes = await api.get('/users/', { params: { role: 'client' } });
        setClients(clientsRes.data);
      }
      
      const payload = {
        ...newProject,
        client_id: clientId,
        estimated_hours: parseFloat(newProject.estimated_hours) || 0
      };
      
      await api.post('/projects/', payload);
      toast.success('Projet créé avec succès !');
      setShowNewProject(false);
      setNewProject({
        name: '',
        project_type: 'logo',
        client_id: '',
        description: '',
        estimated_hours: ''
      });
      setNewClient({
        first_name: '',
        last_name: '',
        email: '',
        company_name: '',
        phone: ''
      });
      setClientMode('existing');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création');
    } finally {
      setCreating(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      draft: 'bg-gray-100 text-gray-700',
      brief_pending: 'bg-yellow-100 text-yellow-700',
      brief_signed: 'bg-blue-100 text-blue-700',
      in_progress: 'bg-purple-100 text-purple-700',
      review: 'bg-orange-100 text-orange-700',
      delivered: 'bg-green-100 text-green-700',
      completed: 'bg-emerald-100 text-emerald-700',
      cancelled: 'bg-red-100 text-red-700',
    };
    const labels = {
      draft: 'Brouillon',
      brief_pending: 'Brief en attente',
      brief_signed: 'Brief validé',
      in_progress: 'En cours',
      review: 'En révision',
      delivered: 'Livré',
      completed: 'Terminé',
      cancelled: 'Annulé',
    };
    return (
      <Badge className={cn('font-medium', styles[status] || styles.draft)}>
        {labels[status] || status}
      </Badge>
    );
  };

  const projectTypeIcons = {
    logo: '🎨',
    visual: '🖼️',
    video: '🎬',
    website: '🌐',
    mobile_app: '📱',
  };

  const projectTypeLabels = {
    logo: 'Logo',
    visual: 'Visuel',
    video: 'Vidéo',
    website: 'Site web',
    mobile_app: 'App mobile',
  };

  // COPILOTE Badge for clients with active copilote subscription
  const CopiloteBadge = ({ small = false }) => (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className={cn(
            "inline-flex items-center gap-1 rounded-full font-medium",
            small 
              ? "px-1.5 py-0.5 text-[10px]" 
              : "px-2 py-0.5 text-xs",
            "bg-gradient-to-r from-cyan-500/20 to-emerald-500/20 text-cyan-700 border border-cyan-300/50"
          )}>
            <Rocket className={small ? "w-2.5 h-2.5" : "w-3 h-3"} />
            COPILOTE
          </span>
        </TooltipTrigger>
        <TooltipContent>
          <p>Client en accompagnement Copilote</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );

  const filteredProjects = projects.filter(p => {
    const matchesSearch = p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         p.client_name?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = filterStatus === 'all' || p.status === filterStatus;
    const matchesType = filterType === 'all' || p.project_type === filterType;
    return matchesSearch && matchesStatus && matchesType;
  });

  // Helper: Get active workers for a project
  const getActiveWorkers = (projectId) => {
    return activeTimers.filter(t => t.project_id === projectId);
  };

  // Helper: Calculate budget progress percentage
  const getBudgetProgress = (project) => {
    const estimated = project.estimated_hours * (project.hourly_rate || 75);
    const spent = project.spent || 0;
    if (!estimated) return 0;
    return Math.min((spent / estimated) * 100, 100);
  };

  // Helper: Calculate task completion percentage
  const getTaskProgress = (project) => {
    if (!project.estimated_hours) return 0;
    return Math.min(((project.actual_hours || 0) / project.estimated_hours) * 100, 100);
  };

  // Helper: Calculate margin
  const getMargin = (project) => {
    const estimated = project.estimated_hours * (project.hourly_rate || 75);
    const spent = project.spent || 0;
    return estimated - spent;
  };

  // Helper: Get progress bar color based on percentage
  const getProgressColor = (percentage, isInverse = false) => {
    if (isInverse) {
      // For budget: green when low, red when high
      if (percentage >= 90) return 'bg-red-500';
      if (percentage >= 70) return 'bg-orange-500';
      if (percentage >= 50) return 'bg-yellow-500';
      return 'bg-emerald-500';
    } else {
      // For tasks: red when low, green when high
      if (percentage >= 80) return 'bg-emerald-500';
      if (percentage >= 50) return 'bg-yellow-500';
      if (percentage >= 25) return 'bg-orange-500';
      return 'bg-red-500';
    }
  };

  // Progress Gauge Component
  const ProgressGauge = ({ percentage, label, isInverse = false, showLabel = true }) => {
    const color = getProgressColor(percentage, isInverse);
    return (
      <div className="w-full">
        {showLabel && (
          <div className="flex justify-between text-xs text-[#64748b] mb-1">
            <span>{label}</span>
            <span className="font-medium">{Math.round(percentage)}%</span>
          </div>
        )}
        <div className="h-2 bg-[#e2e8f0] rounded-full overflow-hidden">
          <div 
            className={cn("h-full rounded-full transition-all duration-500", color)}
            style={{ width: `${percentage}%` }}
          />
        </div>
      </div>
    );
  };

  // Active Worker Avatar Component
  const ActiveWorkerAvatar = ({ worker, index }) => (
    <TooltipProvider key={worker.user_id}>
      <Tooltip>
        <TooltipTrigger asChild>
          <div 
            className={cn(
              "w-8 h-8 rounded-full border-2 border-white flex items-center justify-center text-xs font-medium cursor-pointer relative",
              "bg-gradient-to-br from-emerald-400 to-emerald-600 text-white shadow-md"
            )}
            style={{ marginLeft: index > 0 ? '-8px' : '0', zIndex: 10 - index }}
          >
            {worker.user_name?.split(' ').map(n => n[0]).join('') || '?'}
            {/* Pulsing indicator for active timer */}
            <span className="absolute -top-0.5 -right-0.5 w-3 h-3 bg-green-400 rounded-full border-2 border-white animate-pulse" />
          </div>
        </TooltipTrigger>
        <TooltipContent>
          <div className="text-center">
            <p className="font-medium">{worker.user_name}</p>
            <p className="text-xs text-green-400 flex items-center gap-1">
              <Play className="w-3 h-3" /> En cours depuis {Math.round((worker.duration_minutes || 0))} min
            </p>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );

  return (
    <div className="min-h-screen bg-[#f8fafc]">
      <Sidebar 
        collapsed={collapsed} 
        setCollapsed={setCollapsed}
        mobileOpen={mobileOpen}
        setMobileOpen={setMobileOpen}
      />
      <Header collapsed={collapsed} setMobileOpen={setMobileOpen} />
      
      <main className={cn(
        "transition-all duration-300 px-6 md:px-8 pb-6 md:pb-8 pt-28 md:pt-32",
        collapsed ? "md:ml-20" : "md:ml-64"
      )}>
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold text-[#1a365d]" data-testid="projects-title">
              {isAgency ? 'Tous les projets' : 'Mes projets'}
            </h1>
            <p className="text-[#64748b] mt-1">
              {filteredProjects.length} projet(s)
            </p>
          </div>
          
          {isAgency && (
            <Dialog open={showNewProject} onOpenChange={setShowNewProject}>
              <DialogTrigger asChild>
                <Button className="btn-primary" data-testid="new-project-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Nouveau projet
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-lg">
                <DialogHeader>
                  <DialogTitle>Créer un projet</DialogTitle>
                </DialogHeader>
                <form onSubmit={createProject} className="space-y-4">
                  <div className="space-y-2">
                    <Label>Nom du projet</Label>
                    <Input
                      value={newProject.name}
                      onChange={(e) => setNewProject({ ...newProject, name: e.target.value })}
                      placeholder="Refonte site web ABC Corp"
                      className="input-default"
                      required
                      data-testid="project-name-input"
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Type de projet</Label>
                      <Select 
                        value={newProject.project_type} 
                        onValueChange={(v) => setNewProject({ ...newProject, project_type: v })}
                      >
                        <SelectTrigger className="input-default" data-testid="project-type-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="logo">🎨 Logo</SelectItem>
                          <SelectItem value="visual">🖼️ Visuel</SelectItem>
                          <SelectItem value="video">🎬 Vidéo</SelectItem>
                          <SelectItem value="website">🌐 Site web</SelectItem>
                          <SelectItem value="mobile_app">📱 App mobile</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div className="space-y-2">
                      <Label>Heures estimées</Label>
                      <Input
                        type="number"
                        value={newProject.estimated_hours}
                        onChange={(e) => setNewProject({ ...newProject, estimated_hours: e.target.value })}
                        placeholder="10"
                        className="input-default"
                        data-testid="project-hours-input"
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Client</Label>
                    <Tabs value={clientMode} onValueChange={setClientMode} className="w-full">
                      <TabsList className="grid w-full grid-cols-2">
                        <TabsTrigger value="existing" className="text-xs">
                          <Users className="w-3 h-3 mr-1" />
                          Client existant
                        </TabsTrigger>
                        <TabsTrigger value="new" className="text-xs">
                          <UserPlus className="w-3 h-3 mr-1" />
                          Nouveau client
                        </TabsTrigger>
                      </TabsList>
                      <TabsContent value="existing" className="mt-2">
                        <Select 
                          value={newProject.client_id} 
                          onValueChange={(v) => setNewProject({ ...newProject, client_id: v })}
                        >
                          <SelectTrigger className="input-default" data-testid="project-client-select">
                            <SelectValue placeholder="Sélectionner un client" />
                          </SelectTrigger>
                          <SelectContent>
                            {clients.map((client) => (
                              <SelectItem key={client.id} value={client.id}>
                                {client.first_name} {client.last_name}
                                {client.company_name && ` (${client.company_name})`}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </TabsContent>
                      <TabsContent value="new" className="mt-2 space-y-3">
                        <div className="grid grid-cols-2 gap-2">
                          <Input
                            value={newClient.first_name}
                            onChange={(e) => setNewClient({ ...newClient, first_name: e.target.value })}
                            placeholder="Prénom"
                            className="input-default"
                          />
                          <Input
                            value={newClient.last_name}
                            onChange={(e) => setNewClient({ ...newClient, last_name: e.target.value })}
                            placeholder="Nom"
                            className="input-default"
                          />
                        </div>
                        <Input
                          type="email"
                          value={newClient.email}
                          onChange={(e) => setNewClient({ ...newClient, email: e.target.value })}
                          placeholder="Email *"
                          className="input-default"
                          required={clientMode === 'new'}
                        />
                        <div className="grid grid-cols-2 gap-2">
                          <Input
                            value={newClient.company_name}
                            onChange={(e) => setNewClient({ ...newClient, company_name: e.target.value })}
                            placeholder="Entreprise"
                            className="input-default"
                          />
                          <Input
                            value={newClient.phone}
                            onChange={(e) => setNewClient({ ...newClient, phone: e.target.value })}
                            placeholder="Téléphone"
                            className="input-default"
                          />
                        </div>
                      </TabsContent>
                    </Tabs>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Description</Label>
                    <Textarea
                      value={newProject.description}
                      onChange={(e) => setNewProject({ ...newProject, description: e.target.value })}
                      placeholder="Description du projet..."
                      className="input-default min-h-[100px]"
                      data-testid="project-description-input"
                    />
                  </div>
                  
                  <div className="flex justify-end gap-3">
                    <Button type="button" variant="outline" onClick={() => setShowNewProject(false)}>
                      Annuler
                    </Button>
                    <Button type="submit" className="btn-primary" disabled={creating}>
                      {creating ? 'Création...' : 'Créer le projet'}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>

        {/* Filters */}
        <Card className="card mb-6">
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748b]" />
                <Input
                  placeholder="Rechercher un projet..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="input-default pl-10"
                  data-testid="search-projects-input"
                />
              </div>
              
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="input-default w-full md:w-[180px]" data-testid="filter-status">
                  <Filter className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Statut" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les statuts</SelectItem>
                  <SelectItem value="draft">Brouillon</SelectItem>
                  <SelectItem value="brief_pending">Brief en attente</SelectItem>
                  <SelectItem value="brief_signed">Brief validé</SelectItem>
                  <SelectItem value="in_progress">En cours</SelectItem>
                  <SelectItem value="review">En révision</SelectItem>
                  <SelectItem value="delivered">Livré</SelectItem>
                  <SelectItem value="completed">Terminé</SelectItem>
                </SelectContent>
              </Select>
              
              <Select value={filterType} onValueChange={setFilterType}>
                <SelectTrigger className="input-default w-full md:w-[180px]" data-testid="filter-type">
                  <SelectValue placeholder="Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les types</SelectItem>
                  <SelectItem value="logo">🎨 Logo</SelectItem>
                  <SelectItem value="visual">🖼️ Visuel</SelectItem>
                  <SelectItem value="video">🎬 Vidéo</SelectItem>
                  <SelectItem value="website">🌐 Site web</SelectItem>
                  <SelectItem value="mobile_app">📱 App mobile</SelectItem>
                </SelectContent>
              </Select>

              {/* View Mode Toggle - Hidden on mobile */}
              <div className="hidden md:flex items-center border rounded-lg overflow-hidden">
                <Button
                  variant="ghost"
                  size="sm"
                  className={cn(
                    "rounded-none px-3",
                    viewMode === 'table' && "bg-[#1a365d] text-white hover:bg-[#1a365d]"
                  )}
                  onClick={() => setViewMode('table')}
                >
                  <Table2 className="w-4 h-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className={cn(
                    "rounded-none px-3",
                    viewMode === 'grid' && "bg-[#1a365d] text-white hover:bg-[#1a365d]"
                  )}
                  onClick={() => setViewMode('grid')}
                >
                  <LayoutGrid className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Projects Display */}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="spinner" />
          </div>
        ) : filteredProjects.length === 0 ? (
          <Card className="card">
            <CardContent className="p-12 text-center">
              <FolderKanban className="w-16 h-16 mx-auto mb-4 text-[#64748b] opacity-50" />
              <h3 className="text-lg font-semibold text-[#1a365d] mb-2">Aucun projet trouvé</h3>
              <p className="text-[#64748b]">
                {searchQuery || filterStatus !== 'all' || filterType !== 'all'
                  ? 'Essayez de modifier vos filtres'
                  : 'Créez votre premier projet pour commencer'}
              </p>
            </CardContent>
          </Card>
        ) : viewMode === 'table' ? (
          /* TABLE VIEW */
          <Card className="card overflow-hidden">
            <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-[#f8fafc]">
                  <TableHead className="font-semibold text-[#1a365d] whitespace-nowrap">Projet</TableHead>
                  {isAgency && <TableHead className="font-semibold text-[#1a365d] whitespace-nowrap">Client</TableHead>}
                  <TableHead className="font-semibold text-[#1a365d] whitespace-nowrap">Statut</TableHead>
                  <TableHead className="font-semibold text-[#1a365d] w-[180px] whitespace-nowrap">
                    {isTeamMember ? 'Temps accordé' : 'Progression'}
                  </TableHead>
                  {isAgency && (
                    <>
                      <TableHead className="font-semibold text-[#1a365d] w-[180px] whitespace-nowrap">Budget</TableHead>
                      <TableHead className="font-semibold text-[#1a365d] text-right whitespace-nowrap">Marge</TableHead>
                    </>
                  )}
                  <TableHead className="font-semibold text-[#1a365d] whitespace-nowrap">Équipe active</TableHead>
                  <TableHead className="w-[50px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredProjects.map((project) => {
                  const taskProgress = getTaskProgress(project);
                  const budgetProgress = getBudgetProgress(project);
                  const margin = getMargin(project);
                  const activeWorkers = getActiveWorkers(project.id);
                  
                  return (
                    <TableRow 
                      key={project.id}
                      className="cursor-pointer hover:bg-[#f8fafc] transition-colors"
                      onClick={() => navigate(`/projects/${project.id}`)}
                    >
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-[#f1f5f9] rounded-lg flex items-center justify-center text-lg">
                            {projectTypeIcons[project.project_type] || '📁'}
                          </div>
                          <div>
                            <p className="font-medium text-[#1a365d]">{project.name}</p>
                            <p className="text-xs text-[#64748b]">
                              {projectTypeLabels[project.project_type]}
                            </p>
                          </div>
                        </div>
                      </TableCell>
                      {isAgency && (
                        <TableCell>
                          <p className="text-sm text-[#334155]">{project.client_name || '-'}</p>
                        </TableCell>
                      )}
                      <TableCell>
                        {getStatusBadge(project.status)}
                      </TableCell>
                      <TableCell>
                        {isTeamMember ? (
                          /* Member sees only allocated time budget */
                          <div className="text-center">
                            <span className="text-lg font-mono font-bold text-[#1a365d]">
                              {project.estimated_hours || 0}h
                            </span>
                            <p className="text-xs text-[#64748b]">
                              {(project.actual_hours || 0).toFixed(1)}h utilisées
                            </p>
                          </div>
                        ) : (
                          <ProgressGauge 
                            percentage={taskProgress} 
                            label={`${(project.actual_hours || 0).toFixed(1)}h / ${project.estimated_hours}h`}
                            isInverse={false}
                          />
                        )}
                      </TableCell>
                      {isAgency && (
                        <>
                          <TableCell>
                            <ProgressGauge 
                              percentage={budgetProgress} 
                              label={`${(project.spent || 0).toLocaleString('fr-FR')}€ dépensés`}
                              isInverse={true}
                            />
                          </TableCell>
                          <TableCell className="text-right">
                            <div className={cn(
                              "flex items-center justify-end gap-1 font-mono font-bold",
                              margin >= 0 ? "text-emerald-600" : "text-red-600"
                            )}>
                              {margin >= 0 ? (
                                <TrendingUp className="w-4 h-4" />
                              ) : (
                                <TrendingDown className="w-4 h-4" />
                              )}
                              {margin >= 0 ? '+' : ''}{margin.toLocaleString('fr-FR')}€
                            </div>
                          </TableCell>
                        </>
                      )}
                      <TableCell>
                        {activeWorkers.length > 0 ? (
                          <div className="flex items-center">
                            {activeWorkers.slice(0, 3).map((worker, idx) => (
                              <ActiveWorkerAvatar key={worker.user_id} worker={worker} index={idx} />
                            ))}
                            {activeWorkers.length > 3 && (
                              <span className="ml-2 text-xs text-[#64748b]">
                                +{activeWorkers.length - 3}
                              </span>
                            )}
                          </div>
                        ) : (
                          <span className="text-xs text-[#94a3b8]">-</span>
                        )}
                      </TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon">
                              <MoreHorizontal className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => navigate(`/projects/${project.id}`)}>
                              Voir les détails
                            </DropdownMenuItem>
                            <DropdownMenuItem>Modifier</DropdownMenuItem>
                            {isAgency && <DropdownMenuItem>Assigner équipe</DropdownMenuItem>}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            </div>
          </Card>
        ) : (
          /* GRID VIEW */
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredProjects.map((project) => {
              const taskProgress = getTaskProgress(project);
              const budgetProgress = getBudgetProgress(project);
              const margin = getMargin(project);
              const activeWorkers = getActiveWorkers(project.id);
              
              return (
                <Card 
                  key={project.id}
                  className="card card-hover cursor-pointer group"
                  onClick={() => navigate(`/projects/${project.id}`)}
                  data-testid={`project-card-${project.id}`}
                >
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="w-12 h-12 bg-[#f8fafc] rounded-xl flex items-center justify-center text-2xl">
                        {projectTypeIcons[project.project_type] || '📁'}
                      </div>
                      <div className="flex items-center gap-2">
                        {activeWorkers.length > 0 && (
                          <div className="flex items-center">
                            {activeWorkers.slice(0, 2).map((worker, idx) => (
                              <ActiveWorkerAvatar key={worker.user_id} worker={worker} index={idx} />
                            ))}
                          </div>
                        )}
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                            <Button variant="ghost" size="icon" className="opacity-0 group-hover:opacity-100">
                              <MoreHorizontal className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem>Voir les détails</DropdownMenuItem>
                            <DropdownMenuItem>Modifier</DropdownMenuItem>
                            {isAgency && <DropdownMenuItem>Assigner équipe</DropdownMenuItem>}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>
                    
                    <h3 className="font-semibold text-[#1a365d] mb-1 group-hover:text-[#00d4aa] transition-colors">
                      {project.name}
                    </h3>
                    
                    {isAgency && project.client_name && (
                      <p className="text-sm text-[#64748b] mb-3 flex items-center gap-1">
                        <Users className="w-3 h-3" />
                        {project.client_name}
                      </p>
                    )}
                    
                    <div className="flex items-center gap-2 mb-4">
                      {getStatusBadge(project.status)}
                      <Badge variant="outline" className="text-xs">
                        {projectTypeLabels[project.project_type]}
                      </Badge>
                    </div>
                    
                    {/* Progress Gauges */}
                    <div className="space-y-3 mb-4">
                      <ProgressGauge 
                        percentage={taskProgress} 
                        label="Progression"
                        isInverse={false}
                      />
                      <ProgressGauge 
                        percentage={budgetProgress} 
                        label="Budget"
                        isInverse={true}
                      />
                    </div>

                    {/* Margin Indicator */}
                    <div className={cn(
                      "flex items-center justify-between p-3 rounded-lg",
                      margin >= 0 ? "bg-emerald-50" : "bg-red-50"
                    )}>
                      <span className="text-sm text-[#64748b]">Marge estimée</span>
                      <div className={cn(
                        "flex items-center gap-1 font-mono font-bold",
                        margin >= 0 ? "text-emerald-600" : "text-red-600"
                      )}>
                        {margin >= 0 ? (
                          <TrendingUp className="w-4 h-4" />
                        ) : (
                          <TrendingDown className="w-4 h-4" />
                        )}
                        {margin >= 0 ? '+' : ''}{margin.toLocaleString('fr-FR')}€
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
};

export default ProjectsPage;
